"use client";

// A tela que faltava. Até 26/08 o projeto tinha `not-found.tsx` e mais nada:
// qualquer erro não tratado num segmento caía na tela padrão do Next, em inglês
// e sem saída. O PostHog registrou isso uma vez em 31/07, no /universidades:
//
//   UnrecognizedActionError: Server Action "6091..." was not found on the server
//
// Não é bug de lógica. É a aba que ficou aberta enquanto um deploy trocou o
// bundle: o cliente pede uma Server Action que não existe mais no servidor. A
// família vê a página morrer sem entender por quê, e a chance disso subiu agora
// que a gente publica com frequência.
//
// Regra da casa aplicada aqui: nada de emoji, e a voz é a de quem fala com
// família cética. Nada de "algo deu errado" — isso não diz nada e não dá saída.

import { useEffect } from "react";
import Link from "next/link";
import posthog from "posthog-js";

/** O erro é de versão velha, não de lógica? Então recarregar RESOLVE — é a
 *  única classe em que recarregar não é chute. Server Action que sumiu, chunk
 *  que não baixa, import dinâmico que falhou: os três são o mesmo fato, um
 *  bundle novo no servidor e um antigo na aba. */
function ehBundleVelho(err: Error & { digest?: string }): boolean {
  const t = `${err?.name ?? ""} ${err?.message ?? ""} ${err?.digest ?? ""}`;
  return (
    /UnrecognizedActionError/i.test(t) ||
    /Server Action .* was not found/i.test(t) ||
    /ChunkLoadError/i.test(t) ||
    /Loading chunk .* failed/i.test(t) ||
    /Failed to fetch dynamically imported module/i.test(t)
  );
}

const CHAVE_RECARGA = "vb_recarga_por_bundle_velho";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const bundleVelho = ehBundleVelho(error);

  useEffect(() => {
    // O `digest` é o que dá pra cruzar com o log do servidor; a mensagem crua o
    // Next sanitiza em produção. Mandar os dois, e dizer qual caminho seguimos.
    posthog.capture("erro_de_tela", {
      erro_nome: error?.name ?? null,
      erro_mensagem: error?.message ?? null,
      erro_digest: error?.digest ?? null,
      bundle_velho: bundleVelho,
      caminho: typeof window !== "undefined" ? window.location.pathname : null,
    });
  }, [error, bundleVelho]);

  useEffect(() => {
    if (!bundleVelho) return;
    // Guarda contra laço: se recarregar não resolveu, o problema não era versão
    // velha, e recarregar de novo só prende a pessoa num ciclo. Uma tentativa.
    let jaTentou = false;
    try { jaTentou = sessionStorage.getItem(CHAVE_RECARGA) === "1"; } catch { /* modo privado */ }
    if (jaTentou) return;
    try { sessionStorage.setItem(CHAVE_RECARGA, "1"); } catch { /* modo privado */ }
    window.location.reload();
  }, [bundleVelho]);

  // Recarregou e voltou: a partir daqui a marca não serve mais.
  useEffect(() => {
    if (bundleVelho) return;
    try { sessionStorage.removeItem(CHAVE_RECARGA); } catch { /* modo privado */ }
  }, [bundleVelho]);

  return (
    <main
      style={{
        minHeight: "100vh",
        backgroundColor: "#0F1C2E",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        fontFamily: "var(--font-inter, Inter, sans-serif)",
        textAlign: "center",
      }}
    >
      <h1 style={{ fontSize: 26, fontWeight: 800, color: "#ffffff", margin: "0 0 12px", letterSpacing: "-0.5px" }}>
        {bundleVelho ? "Atualizando a página…" : "Esta tela não carregou"}
      </h1>

      <p style={{ fontSize: 15, color: "#94a3b8", maxWidth: 420, lineHeight: 1.6, margin: "0 0 32px" }}>
        {bundleVelho
          ? "Saiu uma versão nova do site enquanto esta aba estava aberta. Estamos recarregando — nada do seu trabalho se perde."
          : "O erro é nosso, não seu. Nada do que você já salvou se perde. Tente de novo; se insistir, me avise pelo WhatsApp e eu olho o caso."}
      </p>

      {!bundleVelho && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={reset}
            style={{
              backgroundColor: "#C49A3C", color: "#0F1C2E", fontWeight: 700, fontSize: 15,
              padding: "14px 32px", borderRadius: 10, border: "none", cursor: "pointer",
            }}
          >
            Tentar de novo
          </button>
          <Link
            href="/meu-perfil"
            style={{
              color: "#94a3b8", fontWeight: 600, fontSize: 15, padding: "14px 24px",
              borderRadius: 10, textDecoration: "none", border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            Voltar para o meu perfil
          </Link>
        </div>
      )}

      {error?.digest && (
        // Um código curto que a família pode copiar na mensagem. Sem isto, "deu
        // erro" chega sem nada que cruze com o log do servidor.
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", marginTop: 28 }}>
          código {error.digest}
        </p>
      )}
    </main>
  );
}
